import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTwilioVoice } from '@hooks/useTwilioVoice';
import { useIncomingCallTabFlash } from '@hooks/useIncomingCallTabFlash';
import { CallState, TwilioDeviceState, CallEntityType } from '@/types/twilio';
import type { TwilioInitiateCallOptions } from '@api-client/twilio';
import { incomingContactPath } from '@/utils/incomingCallPresentation';
import { useStore } from '@hooks/useStore';
import { CallNotification } from 'stubben-edge-unified-frontend-react-components';
import IncomingCallNotification, {
  INCOMING_ACCEPT_SLIDE_MS
} from './IncomingCallNotification';
import CallKeypad from './CallKeypad';
import DraggableCallPanel from './DraggableCallPanel';
import TransferCallControl from './TransferCallControl';
import './IncomingCallNotification.css';

interface TwilioContextType {
  deviceState: TwilioDeviceState;
  callState: CallState;
  initiateCall: (
    entityId: string,
    phoneNumber: string,
    callerName: string,
    entityType: CallEntityType,
    options?: TwilioInitiateCallOptions
  ) => Promise<void>;
  terminateCall: () => void;
  acceptIncomingCall: () => void;
  rejectIncomingCall: () => void;
  transferCall: (userId: number) => void;
  sendDigits: (digits: string) => void;
  canSendDigits: boolean;
  isTransferring: boolean;
  canTransferCall: boolean;
  getCallStatusText: () => string;
  canMakePhoneCalls: boolean;
}

const TwilioContext = createContext<TwilioContextType | undefined>(undefined);

export function useTwilio() {
  const context = useContext(TwilioContext);
  if (context === undefined) {
    throw new Error('useTwilio must be used within a TwilioProvider');
  }
  return context;
}

interface TwilioProviderProps {
  children: ReactNode;
}

export function TwilioProvider({ children }: TwilioProviderProps) {
  const {
    deviceState,
    callState,
    initiateCall,
    terminateCall,
    acceptIncomingCall,
    rejectIncomingCall,
    transferCall,
    isTransferring,
    canTransferCall,
    sendDigits,
    canSendDigits,
    getCallStatusText
  } = useTwilioVoice();
  const { authStore } = useStore();
  const { user, hasFeature } = authStore;
  const inboundCallingEnabled = hasFeature('inbound_calling');
  const navigate = useNavigate();
  const [isAcceptExiting, setIsAcceptExiting] = useState(false);
  const [isDockedToCorner, setIsDockedToCorner] = useState(false);
  const isIncomingRinging = callState.status === 'incoming';
  const showIncomingSnackbar = inboundCallingEnabled && isIncomingRinging;
  const showActiveCallUi =
    callState.status !== 'idle' &&
    callState.status !== 'disconnected' &&
    !isIncomingRinging;

  useIncomingCallTabFlash(showIncomingSnackbar);

  useEffect(() => {
    if (!isIncomingRinging) {
      setIsAcceptExiting(false);
      setIsDockedToCorner(false);
    }
  }, [isIncomingRinging]);

  const contactPath = incomingContactPath(
    callState.entityType,
    callState.entityId
  );

  const handleAccept = () => {
    if (isAcceptExiting) return;
    setIsAcceptExiting(true);
    window.setTimeout(() => {
      acceptIncomingCall();
    }, INCOMING_ACCEPT_SLIDE_MS);
  };

  const handleCallerNameClick = contactPath
    ? () => {
        setIsDockedToCorner(true);
        navigate(contactPath);
      }
    : undefined;

  return (
    <TwilioContext.Provider
      value={{
        deviceState,
        callState,
        initiateCall,
        terminateCall,
        acceptIncomingCall,
        rejectIncomingCall,
        transferCall,
        isTransferring,
        canTransferCall,
        sendDigits,
        canSendDigits,
        getCallStatusText,
        canMakePhoneCalls: user?.canMakePhoneCalls ?? false
      }}
    >
      {children}
      {showIncomingSnackbar ? (
        <div
          className={`incoming-call-overlay${
            isDockedToCorner ? ' incoming-call-overlay--docked' : ''
          }`}
          data-testid="incoming-call-overlay"
          data-docked={isDockedToCorner ? 'true' : 'false'}
        >
          <div className="incoming-call-overlay__card">
            <IncomingCallNotification
              phoneNumber={callState.phoneNumber || ''}
              callerName={callState.callerName}
              callerAvatarUrl={callState.contactAvatarUrl}
              callGroupName={callState.callGroupName}
              status={getCallStatusText()}
              isExiting={isAcceptExiting}
              onAccept={handleAccept}
              onReject={rejectIncomingCall}
              onCallerNameClick={handleCallerNameClick}
            />
          </div>
        </div>
      ) : null}
      {showActiveCallUi ? (
        <DraggableCallPanel>
          <CallNotification
            callType={
              callState.status === 'connected' ? 'connected' : 'outgoing'
            }
            onHangUp={terminateCall}
            phoneNumber={callState.callerName || callState.phoneNumber || ''}
            status={getCallStatusText()}
            error={callState.error || ''}
          />
          {canSendDigits ? <CallKeypad onDigit={sendDigits} /> : null}
          {inboundCallingEnabled && canTransferCall ? (
            <TransferCallControl
              onTransfer={transferCall}
              isTransferring={isTransferring}
              currentUserId={user?.id}
            />
          ) : null}
        </DraggableCallPanel>
      ) : null}
    </TwilioContext.Provider>
  );
}
